import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { LuBookOpen, LuFileText, LuInfo, LuKeyboard, LuPlay, LuRotateCcw, LuX } from 'react-icons/lu';
import Button from '../../../components/ui/Button';
import GlassIcon from '../../../components/ui/GlassIcon';
import Modal from '../../../components/ui/Modal';
import { useTranslation } from '../../../i18n/LanguageContext';
import GuideControls from './engine/GuideControls';
import GuideStage from './engine/GuideStage';
import GuideIndex from './GuideIndex';
import useGuidePlayer from './useGuidePlayer';
import { useGuideLength } from './useUserGuide';
import DashboardScene from './scenes/DashboardScene';
import ProfileScene from './scenes/ProfileScene';
import TitleScene from './scenes/TitleScene';

const SCENES = { title: TitleScene, dashboard: DashboardScene, profile: ProfileScene };

const PlayerBody = ({ start, onClose }) => {
  const { t } = useTranslation();
  const label = (key, vars) => t(`userGuide.player.${key}`, vars);
  const player = useGuidePlayer(start);
  const { timeline, time, playing, ended, beat, chapter, chapterIndex, fullscreen } = player;
  const [query, setQuery] = useState('');
  const [indexOpen, setIndexOpen] = useState(false);
  const stepNumber = chapter.beats.indexOf(beat) + 1;

  const openChapter = (target) => {
    player.goToChapter(timeline.chapters.indexOf(target));
    setIndexOpen(false);
  };
  const openBeat = (target) => {
    player.goToBeat(target.index);
    setIndexOpen(false);
  };

  return (
    <div className="tw-grid tw-gap-4 lg:tw-grid-cols-[19rem_minmax(0,1fr)] lg:tw-gap-5">
      <GuideIndex
        timeline={timeline}
        time={time}
        chapterIndex={chapterIndex}
        beatIndex={player.index}
        watched={player.watched}
        query={query}
        onQuery={setQuery}
        open={indexOpen}
        onToggle={() => setIndexOpen((value) => !value)}
        onChapter={openChapter}
        onBeat={openBeat}
      />

      <div
        ref={player.frameRef}
        className={fullscreen
          ? 'tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-3 tw-bg-black tw-p-4'
          : 'tw-min-w-0 tw-space-y-3 lg:tw-mx-auto lg:tw-w-full lg:tw-max-w-[calc((88vh-22rem)*16/9)]'}
      >
        <div
          ref={player.stageRef}
          role="button"
          tabIndex={0}
          aria-label={ended ? label('controls.replay') : playing ? label('controls.pause') : label('controls.play')}
          onClick={player.togglePlay}
          onKeyDown={(event) => {
            if (event.key === 'Enter') player.togglePlay();
          }}
          style={fullscreen ? { width: 'min(100%, calc((100vh - 12rem) * 16 / 9))' } : undefined}
          className="tw-relative tw-w-full tw-cursor-pointer tw-overflow-hidden tw-rounded-2xl tw-ring-1 tw-ring-slate-200 focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-teal-500 dark:tw-ring-white/10"
        >
          <GuideStage timeline={timeline} time={time} scenes={SCENES} />
          {!playing && !ended && (
            <span className="tw-absolute tw-inset-0 tw-grid tw-place-items-center tw-bg-slate-950/25">
              <span className="tw-grid tw-h-16 tw-w-16 tw-place-items-center tw-rounded-full tw-bg-white/90 tw-text-teal-600 tw-shadow-2xl sm:tw-h-20 sm:tw-w-20 dark:tw-bg-[#262a34]/95 dark:tw-text-teal-300">
                <LuPlay aria-hidden="true" className="tw-ml-1 tw-h-8 tw-w-8 sm:tw-h-9 sm:tw-w-9" />
              </span>
            </span>
          )}
          {ended && (
            <div className="tw-absolute tw-inset-0 tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-3 tw-bg-slate-950/60 tw-p-4 tw-text-center tw-backdrop-blur-sm sm:tw-gap-4 sm:tw-p-6">
              <p className="tw-text-xl tw-font-bold tw-text-white sm:tw-text-2xl">{label('ended')}</p>
              <p className="tw-max-w-md tw-text-sm tw-text-white/80">{label('endedText')}</p>
              <div className="tw-flex tw-flex-wrap tw-justify-center tw-gap-2" onClick={(event) => event.stopPropagation()} role="presentation">
                <Button onClick={() => player.goToChapter(0)}>
                  <LuRotateCcw aria-hidden="true" className="tw-h-4 tw-w-4" />
                  {label('replay')}
                </Button>
                <Button variant="primary" onClick={onClose}>
                  <LuX aria-hidden="true" className="tw-h-4 tw-w-4" />
                  {label('close')}
                </Button>
              </div>
            </div>
          )}
        </div>

        {player.captionsOn && (
          <div aria-live="polite" className={`tw-w-full tw-rounded-2xl tw-px-4 tw-py-3 ${fullscreen ? 'tw-bg-white/[0.06] tw-text-white' : 'tw-border tw-border-teal-500/20 tw-bg-teal-500/[0.05] dark:tw-border-teal-400/20'}`}>
            <p className={`tw-text-[11px] tw-font-semibold tw-uppercase tw-tracking-[0.12em] ${fullscreen ? 'tw-text-teal-300' : 'tw-text-teal-700 dark:tw-text-teal-300'}`}>
              {label('chapterOf', { current: chapterIndex + 1, total: timeline.chapters.length })} · {t(`userGuide.chapters.${chapter.key}`)}
              {chapter.beats.length > 1 && <span className="tw-normal-case tw-tracking-normal"> · {stepNumber}/{chapter.beats.length}</span>}
            </p>
            <p className={`tw-mt-1 tw-min-h-[3rem] tw-text-[15px] tw-font-medium tw-leading-relaxed sm:tw-text-[17px] ${fullscreen ? 'tw-text-white' : 'tw-text-slate-800 dark:tw-text-slate-100'}`}>{beat.caption}</p>
          </div>
        )}

        <GuideControls
          label={(key) => label(`controls.${key}`)}
          timeline={timeline}
          time={time}
          playing={playing}
          ended={ended}
          rate={player.rate}
          captionsOn={player.captionsOn}
          voiceOn={player.voiceOn}
          volume={player.volume}
          voiceAvailable={player.narration.available}
          fullscreen={fullscreen}
          onTogglePlay={player.togglePlay}
          onSeek={player.jump}
          onPrevChapter={player.prevChapter}
          onNextChapter={player.nextChapter}
          onRate={player.setRate}
          onCaptions={() => player.setCaptionsOn(!player.captionsOn)}
          onVoice={player.toggleMute}
          onVolume={player.changeVolume}
          onFullscreen={player.toggleFullscreen}
        />

        {!fullscreen && (
          <div className="tw-space-y-1.5 tw-px-1 tw-text-xs tw-leading-snug tw-text-slate-500 dark:tw-text-slate-400">
            {!player.narration.available && (
              <p className="tw-flex tw-items-start tw-gap-2"><LuInfo aria-hidden="true" className="tw-mt-0.5 tw-h-3.5 tw-w-3.5 tw-shrink-0" />{label('controls.voiceUnavailable')}</p>
            )}
            <p className="tw-hidden tw-items-start tw-gap-2 sm:tw-flex"><LuKeyboard aria-hidden="true" className="tw-mt-0.5 tw-h-3.5 tw-w-3.5 tw-shrink-0" />{label('keyboard')}</p>
            <p className="tw-flex tw-items-start tw-gap-2"><LuInfo aria-hidden="true" className="tw-mt-0.5 tw-h-3.5 tw-w-3.5 tw-shrink-0" />{label('demoNote')}</p>
          </div>
        )}
      </div>
    </div>
  );
};

PlayerBody.propTypes = { start: PropTypes.object, onClose: PropTypes.func.isRequired };

/**
 * The new-system guide in a dialog: topics and search on the left, the video
 * with captions and controls on the right. Everything resets when it closes
 * except the progress kept in this browser. The old PDF guide stays one click away.
 */
const UserGuidePlayer = ({ isOpen, onClose, start, onOpenOldGuide }) => {
  const { t, language } = useTranslation();
  const length = useGuideLength();
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="full"
      title={t('userGuide.player.title')}
      subtitle={t('userGuide.player.subtitle', { count: length.topics, minutes: length.minutes })}
      closeLabel={t('common.close')}
      lang={language}
      bodyClassName="tw-p-3 sm:tw-p-5"
      icon={(
        <GlassIcon gradient="tw-from-teal-400 tw-to-indigo-600 tw-shadow-teal-500/30" size="xs">
          <LuBookOpen aria-hidden="true" className="tw-h-[18px] tw-w-[18px]" />
        </GlassIcon>
      )}
      actions={(
        <Button size="sm" onClick={onOpenOldGuide}>
          <LuFileText aria-hidden="true" className="tw-h-4 tw-w-4" />
          {t('userGuide.player.oldGuide')}
        </Button>
      )}
    >
      <PlayerBody start={start} onClose={onClose} />
    </Modal>
  );
};

UserGuidePlayer.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  start: PropTypes.object,
  onOpenOldGuide: PropTypes.func.isRequired,
};

export default UserGuidePlayer;
